import { useEffect, useState } from 'react';
import {
  IconEye,
  IconInfoCircle,
  IconLoader2,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import {
  EMAIL_PARAM_COUNT_MAX,
  EMAIL_SUBJECT_MAX,
  EMAIL_SUBJECT_VISIBLE,
  EmailVariant,
  emailVariantError,
} from './email-variant';

/**
 * Email content for a multi-channel campaign.
 *
 * Two things this editor must teach:
 *
 *  - THE BODY IS NOT HERE. It lives in the Brevo template, authored in Brevo's
 *    editor; this screen supplies the id, the subject and the merge params.
 *  - Personalisation IS allowed, unlike push. One Brevo publish goes out per
 *    recipient, so {{first_name}} works in the subject and in every param.
 *
 * The template list is INFORMATIONAL (contract §5): it comes from the brand's
 * Nova config and may legitimately be empty, so the field always accepts a
 * typed id and the list is a convenience rather than a constraint.
 */
export function EmailVariantEditor({
  value,
  onChange,
  disabled,
}: {
  value: EmailVariant;
  onChange: (variant: EmailVariant) => void;
  disabled?: boolean;
}) {
  type Template = { id: number; name: string; subject: string };
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [templateError, setTemplateError] = useState('');
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    let current = true;
    api
      .get('/campaigns/email-templates')
      .then((response) => {
        if (current) setTemplates(response.data.templates ?? []);
      })
      .catch(() => {
        if (current)
          setTemplateError('Could not load campaign templates from Brevo.');
      })
      .finally(() => {
        if (current) setLoadingTemplates(false);
      });
    return () => {
      current = false;
    };
  }, []);

  const set = <K extends keyof EmailVariant>(
    key: K,
    fieldValue: EmailVariant[K],
  ) => onChange({ ...value, [key]: fieldValue });

  const setParam = (index: number, key: string, paramValue: string) =>
    set(
      'params',
      value.params.map((param, position) =>
        position === index ? { key, value: paramValue } : param,
      ),
    );

  const error = emailVariantError(value);
  const subjectOver = value.subject.length > EMAIL_SUBJECT_MAX;
  const subjectLong =
    !subjectOver && value.subject.length > EMAIL_SUBJECT_VISIBLE;

  return (
    <div className="space-y-4 rounded-lg border border-border p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Email</h4>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <IconInfoCircle size={14} />
          {'{{first_name}}, {{surname}} and {{brand}} are supported'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email-subject">Subject</Label>
        <Input
          id="email-subject"
          value={value.subject}
          onChange={(event) => set('subject', event.target.value)}
          placeholder="{{first_name}}, double data this weekend"
          disabled={disabled}
          className={cn(subjectOver && 'border-destructive')}
        />
        {subjectOver ? (
          <p className="text-xs text-destructive">
            {value.subject.length}/{EMAIL_SUBJECT_MAX} characters — too long to
            send.
          </p>
        ) : subjectLong ? (
          <p className="text-xs text-amber-600 dark:text-amber-500">
            {value.subject.length}/{EMAIL_SUBJECT_MAX} characters — most inboxes
            show about the first {EMAIL_SUBJECT_VISIBLE}.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {value.subject.length}/{EMAIL_SUBJECT_MAX} characters
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email-template-id">Brevo Campaign Template</Label>
        <div className="flex gap-2 items-center">
          <select
            id="email-template-id"
            className="flex h-9 w-full max-w-xl rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
            value={value.brevoTemplateId}
            onChange={(event) => {
              const selected = templates.find(
                (item) => String(item.id) === event.target.value,
              );
              onChange({
                ...value,
                brevoTemplateId: event.target.value,
                subject: value.subject || selected?.subject || '',
              });
              setPreviewHtml(null);
            }}
            disabled={disabled || loadingTemplates}
          >
            <option value="">
              {loadingTemplates
                ? 'Loading Brevo templates…'
                : 'Select a template'}
            </option>
            {templates.map((template) => (
              <option key={template.id} value={String(template.id)}>
                {template.name} (#{template.id})
              </option>
            ))}
          </select>
          <Button
            type="button"
            variant="outline"
            disabled={disabled || !value.brevoTemplateId || loadingPreview}
            onClick={async () => {
              setLoadingPreview(true);
              setTemplateError('');
              try {
                const response = await api.get(
                  `/campaigns/email-templates/${value.brevoTemplateId}`,
                );
                setPreviewHtml(response.data.htmlContent ?? '');
              } catch {
                setTemplateError(
                  'Could not load this template preview from Brevo.',
                );
              } finally {
                setLoadingPreview(false);
              }
            }}
          >
            {loadingPreview ? (
              <IconLoader2 size={14} className="animate-spin" />
            ) : (
              <IconEye size={14} />
            )}
            Preview
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Only active Brevo templates tagged campaigns are available.
        </p>
        {templateError && (
          <p className="text-xs text-destructive">{templateError}</p>
        )}
        {previewHtml !== null && (
          <div className="overflow-hidden rounded-md border bg-white">
            <iframe
              title="Brevo email template preview"
              sandbox=""
              srcDoc={previewHtml}
              className="h-[420px] w-full"
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Template Params</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || value.params.length >= EMAIL_PARAM_COUNT_MAX}
            onClick={() =>
              set('params', [...value.params, { key: '', value: '' }])
            }
          >
            <IconPlus size={14} />
            Add Param
          </Button>
        </div>
        {value.params.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No params. Add one for every placeholder the Brevo template expects
            — for example firstName with the value {'{{first_name}}'}.
          </p>
        )}
        {value.params.map((param, index) => (
          <div key={index} className="flex gap-2 items-center">
            <Input
              aria-label={`Param ${index + 1} name`}
              value={param.key}
              onChange={(event) =>
                setParam(index, event.target.value, param.value)
              }
              placeholder="firstName"
              disabled={disabled}
              className="max-w-[12rem]"
            />
            <Input
              aria-label={`Param ${index + 1} value`}
              value={param.value}
              onChange={(event) =>
                setParam(index, param.key, event.target.value)
              }
              placeholder="{{first_name}}"
              disabled={disabled}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={`Remove param ${index + 1}`}
              disabled={disabled}
              onClick={() =>
                set(
                  'params',
                  value.params.filter(
                    (_unused, position) => position !== index,
                  ),
                )
              }
            >
              <IconTrash size={14} />
            </Button>
          </div>
        ))}
      </div>

      {error && <p className="text-xs text-destructive">{error}</p>}

      <p className="text-xs text-muted-foreground flex items-start gap-1">
        <IconInfoCircle size={13} className="mt-0.5 shrink-0" />
        Recipients are deduped on the address, so two customers sharing a
        mailbox receive one email. If Brevo's answer is unclear the recipient is
        recorded as BLOCKED and is never retried automatically — the send may
        already have gone out, so those rows are reconciled by hand rather than
        resent.
      </p>
    </div>
  );
}
